// src/ConsumerApp/Screens/Account/AccountLayout.js

import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { theme } from '../../../assets/theme/theme';
import Navbar from '../../DesignSystem/Navigation/Navbar';
import CenteredContainer from '../../DesignSystem/Layout/CenteredContainer';
import AccountSidebar from './AccountSidebar';
import ArrowLeft from '../../../assets/icons/ArrowLeft.svg';

export default function AccountLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const rawPath = location.pathname.toLowerCase().replace(/\/+$/, '');

  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 900);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 900);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isAccountRoot = rawPath === '/consumer/account';

  return (
    <div style={styles.pageShell}>
      <style>{`
        /* ── Standard Design System Circular Back Button ── */
        .bb-universal-circle-back-btn {
          width: 40px;
          height: 40px;
          border-radius: 999px;
          border: 1px solid #E8E0D0;
          background-color: #FFFFFF;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          flex-shrink: 0;
          padding: 0;
          margin-right: 12px;
          outline: none;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
          transition: transform 0.12s ease, background-color 0.12s ease;
          box-sizing: border-box;
        }

        .bb-universal-circle-back-btn:active {
          transform: scale(0.92);
          background-color: #F8FAFC;
        }

        .bb-universal-circle-back-btn img {
          width: 20px;
          height: 20px;
          display: block;
        }

        /* ══════════════════════════════════════════════════════════
           DESKTOP & TABLET (> 900px): Permanent 2-Column Split
           ══════════════════════════════════════════════════════════ */
        .bb-account-layout-row {
          display: flex;
          gap: 28px;
          padding: 24px 0 48px 0;
          align-items: flex-start;
          width: 100%;
          box-sizing: border-box;
        }

        .bb-account-sidebar-col {
          width: 320px;
          flex-shrink: 0;
          position: sticky;
          top: 96px;
          display: block;
        }

        .bb-account-outlet-col {
          flex: 1;
          min-width: 0;
          display: block;
        }

        .bb-account-mobile-top-bar {
          display: none;
        }

        /* ══════════════════════════════════════════════════════════
           MOBILE (<= 900px): Clean Edge-to-Edge Progressive Hub
           ══════════════════════════════════════════════════════════ */
        @media (max-width: 900px) {
          .bb-account-layout-row {
            flex-direction: column;
            gap: 0;
            padding: 0 !important;
          }

          /* Full-width header for mobile Settings root */
          .bb-account-mobile-top-bar {
            display: flex;
            align-items: center;
            width: 100%;
            height: 56px;
            padding: 0 16px;
            background-color: #FFFFFF;
            border-bottom: 1px solid #F1F5F9;
            box-sizing: border-box;
            position: sticky;
            top: 0;
            z-index: 1000;
          }

          .bb-account-mobile-top-bar h2 {
            margin: 0;
            font-family: ${theme.typography.headingMD.fontFamily};
            font-size: 18px;
            font-weight: 800;
            color: #0F172A;
            letter-spacing: -0.2px;
          }

          .bb-account-sidebar-col {
            width: 100% !important;
            max-width: 100% !important;
            padding: 12px 16px 36px 16px !important;
            box-sizing: border-box !important;
            position: static !important;
            display: ${isAccountRoot ? 'block' : 'none'} !important;
          }

          .bb-account-outlet-col {
            width: 100% !important;
            padding: 0 0 36px 0 !important;
            box-sizing: border-box !important;
            display: ${isAccountRoot ? 'none' : 'block'} !important;
          }
        }
      `}</style>

      {/* Desktop/Tablet Navbar (auto-hidden on mobile account routes) */}
      <Navbar />

      {/* Mobile Top Header: Circular Back Arrow to Home with replace: true */}
      {isMobile && isAccountRoot && (
        <div className="bb-account-mobile-top-bar">
          <button
            type="button"
            className="bb-universal-circle-back-btn"
            onClick={() => navigate('/consumer/HomeScreen', { replace: true })}
            aria-label="Back to Home"
          >
            <img src={ArrowLeft} alt="Back" />
          </button>
          <h2>Account</h2>
        </div>
      )}

      {/* Desktop uses CenteredContainer; Mobile uses full-bleed flush edges */}
      {!isMobile ? (
        <CenteredContainer>
          <div className="bb-account-layout-row">
            <aside className="bb-account-sidebar-col">
              <AccountSidebar />
            </aside>
            <main className="bb-account-outlet-col">
              <Outlet />
            </main>
          </div>
        </CenteredContainer>
      ) : (
        <div className="bb-account-layout-row">
          {isAccountRoot && (
            <aside className="bb-account-sidebar-col">
              <AccountSidebar />
            </aside>
          )}
          {!isAccountRoot && (
            <main className="bb-account-outlet-col">
              <Outlet />
            </main>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  pageShell: {
    backgroundColor: '#FAF9F6',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: theme.typography.bodyMD.fontFamily || 'system-ui, -apple-system, sans-serif',
  },
};